import React, { useEffect, useState } from "react";
import axios from "axios";
import { Boxes, RefreshCw, Filter, Plus, Minus, X } from "lucide-react";
import ProductHoverPreview from "../../components/ProductHoverPreview";
import { useAuth } from "../../context/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminStock() {
  const [stock, setStock] = useState([]);
  const [summary, setSummary] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [inventoryValue, setInventoryValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [warehouseId, setWarehouseId] = useState("");
  const [brand, setBrand] = useState("");
  const [includeZero, setIncludeZero] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null); // stock row to adjust
  const { user } = useAuth();
  // Себестоимость и суммы — только владелец и бухгалтер.
  const canCosts = ["admin", "accountant"].includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (warehouseId) params.set("warehouse_id", warehouseId);
      if (brand) params.set("brand", brand);
      if (includeZero) params.set("include_zero", "true");
      const [s, sum, wh] = await Promise.all([
        axios.get(`${API}/admin/stock?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/admin/stock/summary`, { withCredentials: true }),
        axios.get(`${API}/admin/warehouses`, { withCredentials: true }),
      ]);
      setStock(s.data || []);
      setSummary(sum.data);
      setWarehouses(wh.data || []);
      if (canCosts) {
        try {
          const iv = await axios.get(`${API}/admin/reports/inventory-value`, { withCredentials: true });
          setInventoryValue(iv.data);
        } catch { /* нет доступа к себестоимости */ }
      }
    } catch (e) {
      // 403 → non-finance role; show empty state
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [warehouseId, brand, includeZero]);

  const brands = [...new Set(stock.map(s => s.brand).filter(Boolean))];

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 28, color: "#111", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
            <Boxes size={26} color="#C5A059" /> Остатки на складе
          </h2>
          <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginTop: 4 }}>
            {inventoryValue && (
              <>Всего <strong>{inventoryValue.total_qty}</strong> ед. на сумму <strong>{Number(inventoryValue.total_value_kzt).toLocaleString("ru-RU")} ₸</strong> (по себестоимости)</>
            )}
          </div>
        </div>
        <button onClick={load} style={btnSec} data-testid="stock-refresh"><RefreshCw size={13} /> Обновить</button>
      </div>

      {/* Summary tiles */}
      {summary && summary.rows.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12, marginBottom: 20 }}>
          {summary.rows.slice(0, 8).map((r, i) => (
            <div key={i} style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4 }}>{r.brand}</div>
              <div style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", fontWeight: 600, marginTop: 4 }}>{r.total_qty} ед.</div>
              {canCosts && (
                <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#16a34a", marginTop: 2 }}>{Number(r.total_value_kzt).toLocaleString("ru-RU")} ₸</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginBottom: 14, display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "Manrope", fontSize: 12, color: "#64748b" }}>
          <Filter size={13} /> Фильтры:
        </div>
        <select data-testid="stock-filter-warehouse" value={warehouseId} onChange={e => setWarehouseId(e.target.value)} style={sel}>
          <option value="">Все склады</option>
          {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <select data-testid="stock-filter-brand" value={brand} onChange={e => setBrand(e.target.value)} style={sel}>
          <option value="">Все бренды</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "Manrope", fontSize: 12, color: "#64748b", cursor: "pointer" }}>
          <input type="checkbox" checked={includeZero} onChange={e => setIncludeZero(e.target.checked)} /> Показать с нулём
        </label>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope" }}>Загрузка…</div>
      ) : stock.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope", fontSize: 13, background: "white", borderRadius: 8, border: "1px dashed #e5e7eb" }}>
          Остатков нет. Создайте поступление в разделе «Поступления» → проведите его — товары появятся здесь.
        </div>
      ) : (
        <div style={{ background: "white", borderRadius: 8, border: "1px solid #e5e7eb", overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: 13 }}>
            <thead style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
              <tr>
                <th style={th}>SKU</th>
                <th style={th}>Фабричный</th>
                <th style={th}>Товар</th>
                <th style={th}>Бренд</th>
                <th style={th}>Размер</th>
                <th style={{ ...th, textAlign: "center" }}>Остаток</th>
                {canCosts && <th style={{ ...th, textAlign: "right" }}>Себест./ед</th>}
                {canCosts && <th style={{ ...th, textAlign: "right" }}>Сумма</th>}
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {stock.map(s => {
                const total = (s.qty_on_hand || 0) * (s.avg_cost_kzt || 0);
                return (
                  <tr key={s.id} data-testid={`stock-row-${s.internal_sku}`} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 12, fontWeight: 600, color: "#C5A059" }}>{s.internal_sku || "—"}</td>
                    <td style={{ ...td, color: "#64748b", fontFamily: "monospace", fontSize: 11 }}>{s.factory_sku || "—"}</td>
                    <td style={td}>
                      <ProductHoverPreview
                        productId={s.product_id}
                        fallbackName={s.product_name}
                        fallbackSku={s.internal_sku}
                      >
                        {s.product_name || "—"}
                      </ProductHoverPreview>
                    </td>
                    <td style={{ ...td, color: "#64748b" }}>{s.brand || "—"}</td>
                    <td style={{ ...td, color: "#64748b" }}>{s.size || "—"}</td>
                    <td style={{ ...td, textAlign: "center", fontWeight: 700, color: (s.qty_on_hand || 0) > 0 ? "#16a34a" : "#dc2626" }}>{s.qty_on_hand || 0}</td>
                    {canCosts && <td style={{ ...td, textAlign: "right", color: "#64748b" }}>{Number(s.avg_cost_kzt || 0).toLocaleString("ru-RU")} ₸</td>}
                    {canCosts && <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{Number(total).toLocaleString("ru-RU")} ₸</td>}
                    <td style={{ ...td, textAlign: "right" }}>
                      <button data-testid={`stock-adjust-${s.internal_sku}`} onClick={() => setAdjustFor(s)} style={btnIcon} title="Корректировать остаток">
                        <Plus size={14} /><Minus size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {adjustFor && <AdjustModal row={adjustFor} onClose={() => { setAdjustFor(null); load(); }} />}
    </div>
  );
}

function AdjustModal({ row, onClose }) {
  const [delta, setDelta] = useState(-1);
  const [kind, setKind] = useState("sale");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/stock/adjustment`, {
        warehouse_id: row.warehouse_id,
        product_id: row.product_id,
        internal_sku: row.internal_sku,
        size: row.size,
        delta: parseInt(delta, 10),
        kind, notes,
      }, { withCredentials: true });
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || "Ошибка");
    } finally { setBusy(false); }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div data-testid="stock-adjust-modal" style={modalBox} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 22, color: "#111", margin: 0 }}>
            Корректировка: {row.internal_sku}
          </h3>
          <button onClick={onClose} style={btnIcon}><X size={16} /></button>
        </div>
        <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginBottom: 14 }}>
          {row.product_name} · размер {row.size || "—"} · сейчас на складе: <strong>{row.qty_on_hand}</strong>
        </div>

        <label style={lbl}>Тип операции
          <select data-testid="adjust-kind" value={kind} onChange={e => setKind(e.target.value)} style={sel}>
            <option value="sale">Продажа клиенту</option>
            <option value="refund">Возврат от клиента</option>
            <option value="manual_writeoff">Списание (брак / потеря)</option>
            <option value="manual_addition">Ручное добавление</option>
            <option value="audit_adjustment">Корректировка по переучёту</option>
          </select>
        </label>

        <label style={lbl}>Количество (минус = списание, плюс = приход)
          <input data-testid="adjust-delta" type="number" value={delta} onChange={e => setDelta(e.target.value)} style={inp} />
        </label>

        <label style={lbl}>Комментарий (необязательно)
          <textarea data-testid="adjust-notes" value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontFamily: "Manrope" }} />
        </label>

        {error && <div style={errBox}>{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={btnSec}>Отмена</button>
          <button data-testid="adjust-submit" onClick={submit} disabled={busy} style={btnPrim}>{busy ? "Сохранение…" : "Применить"}</button>
        </div>
      </div>
    </div>
  );
}

const th = { textAlign: "left", padding: "10px 14px", color: "#374151", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 };
const td = { padding: "12px 14px", color: "#111" };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 };
const modalBox = { background: "white", borderRadius: 10, padding: 24, width: "100%", maxWidth: 460, maxHeight: "90vh", overflowY: "auto", fontFamily: "Manrope" };
const lbl = { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 12 };
const inp = { width: "100%", boxSizing: "border-box", border: "1px solid #d1d5db", borderRadius: 6, padding: "9px 12px", fontSize: 13, marginTop: 6, outline: "none" };
const sel = { ...inp, cursor: "pointer", background: "white" };
const btnPrim = { background: "linear-gradient(135deg,#C5A059,#D4B878)", color: "#0A0A0A", border: "none", borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 700, fontSize: 13 };
const btnSec = { background: "white", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontFamily: "Manrope", fontWeight: 600, fontSize: 12, display: "inline-flex", alignItems: "center", gap: 6 };
const btnIcon = { background: "transparent", border: "none", cursor: "pointer", color: "#64748b", padding: 4, display: "inline-flex", alignItems: "center" };
const errBox = { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "8px 12px", borderRadius: 6, marginTop: 10, fontSize: 13 };
